export type Language = 'he' | 'en';
export type TradeDirection = 'long' | 'short';
export type AccountType = 'personal' | 'prop_firm' | 'demo';
export type MarketType = 'futures' | 'forex';
export type BrokerName = 'manual' | 'tradovate' | 'topstepx' | 'mt5';
export type FieldType = 'checkbox' | 'text' | 'select' | 'tag';
export type TradeSource = 'manual' | 'tradovate' | 'topstepx' | 'mt5';

export interface Account {
  id: string;
  user_id?: string;
  name: string;
  account_type: AccountType;
  market_type?: MarketType; // 'futures' (default) | 'forex' — drives instrument list + terminology
  broker: BrokerName;
  initial_balance: number;
  currency: string;
  is_active: boolean;
  // Prop Firm fields (optional, only for account_type === 'prop_firm')
  prop_phase?: 'challenge' | 'funded';
  prop_drawdown_type?: 'trailing_eod' | 'trailing_intraday' | 'static';
  prop_max_drawdown?: number; // absolute $ amount
  prop_daily_limit?: number; // absolute $ amount
  prop_profit_target?: number; // absolute $ amount (challenge only)
  prop_min_days?: number; // minimum trading days (challenge only)
  prop_max_days?: number; // maximum trading days (challenge only)
  prop_start_date?: string; // ISO date string
  prop_consistency_pct?: number; // funded payout consistency threshold % (0 or unset = disabled)
  prop_challenge_consistency_pct?: number; // challenge pass consistency threshold % (0 or unset = disabled)
  // Payout-forecast inputs (simulatePayouts; all optional → typical defaults)
  payout_split_pct?: number; // user's share of withdrawn profit, % (e.g. 90)
  payout_cycle_days?: number; // trading days between payout windows
  payout_buffer?: number; // profit cushion left in the account each payout ($)
  payout_min?: number; // min withdrawable profit to trigger a payout ($)
  payout_max?: number; // cap per payout ($); 0/unset = no cap
}

export interface StrategyField {
  id: string;
  strategy_id: string;
  field_type: FieldType;
  label: string;
  placeholder?: string;
  options?: string[];
  is_required: boolean;
  sort_order: number;
}

export interface Strategy {
  id: string;
  user_id?: string;
  name: string;
  description?: string;
  color: string;
  is_active: boolean;
  fields: StrategyField[];
}

export interface Setup {
  id: string;
  user_id?: string;
  name: string;
  symbols: string[];
  emoji: string;
  hue: number; // 0-360, drives oklch gradient
  description: string;
  rules_entry: string[];
  rules_exit: string[];
  rules_risk: string[];
  is_active: boolean;
  ai_tagged: boolean;
  created_at: string;
  updated_at: string;
}

export interface SetupStats {
  setupId: string;
  trades: number;
  winRate: number; // 0-100
  expectancy: number; // in $
  totalPnl: number;
  avgWin: number;
  avgLoss: number;
  bestHour: string; // e.g. '09:00–10:00'
  equityCurve: number[]; // cumulative PnL over time, one value per trade
  heatmap: number[][]; // [dayOfWeek 0=Mon..4=Fri][hour 0-23] → avg PnL for cell
}

export interface Trade {
  id: string;
  user_id?: string;
  account_id: string;
  strategy_id?: string;
  symbol: string;
  direction: TradeDirection;
  trade_date: string;
  entry_time?: string; // HH:MM:SS
  exit_time?: string; // HH:MM:SS
  entry_price?: number;
  exit_price?: number;
  commission?: number;
  pnl: number;
  size?: number;
  stop_loss_pts?: number;
  take_profit_pts?: number;
  mfe_dollars?: number;
  setup_id?: string;
  discipline_checked?: string[];
  discipline_skipped?: string[];
  discipline_score?: number; // 0.0–1.0, null = no checklist used
  tags?: string[]; // free-form + predefined behavioral tags
  htf_pd_array?: string;
  psychology?: string;
  notes?: string;
  confirmations: Record<string, boolean>;
  field_values: Record<string, unknown>;
  source: TradeSource;
  broker_trade_id?: string;
  media?: TradeMedia[];
}

export interface TradeMedia {
  id: string;
  trade_id: string;
  storage_path: string;
  label?: string;
}

export interface DayStats {
  dateStr: string;
  trades: Trade[];
  totalPnL: number;
  wins: number;
  losses: number;
}

export interface AppStats {
  totalPnL: number;
  winRate: number;
  totalTrades: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
}

export type ModalState =
  | { type: 'new'; date?: string }
  | { type: 'edit'; tradeId: string }
  | { type: 'account'; accountId?: string }
  | { type: 'strategy'; strategyId?: string };

export interface BrokerConnection {
  id: string;
  account_id: string;
  broker: BrokerName;
  is_active: boolean;
  last_synced_at?: string;
}

export type ExpenseFeeType = 'challenge' | 'reset' | 'activation' | 'data_fee' | 'other';

export interface PropExpense {
  id: string;
  user_id?: string;
  account_id?: string; // optional link to a specific account
  prop_firm: string; // e.g. "TopstepX", "Apex"
  account_size: number; // e.g. 50000, 100000, 150000
  fee_type: ExpenseFeeType;
  amount: number; // USD, always positive
  date: string; // ISO date YYYY-MM-DD
  notes?: string;
}

export interface PropPayout {
  id: string;
  user_id?: string;
  account_id?: string;
  prop_firm: string;
  amount: number; // net payout after firm split, USD
  date: string; // ISO date
  notes?: string;
}

export interface BudgetSettings {
  amount: number;
  period: 'monthly' | 'quarterly';
  currency: 'USD' | 'ILS';
  ilsRate: number;
}

export interface CalendarPrefs {
  showWr: boolean; // WR% / BE pill (top-right of each day)
  showPnl: boolean; // daily P&L value
  showSymbols: boolean; // symbol chips (▲NQ…) at cell bottom
  showWeekCol: boolean; // right-side week summary column
}

export const DEFAULT_CALENDAR_PREFS: CalendarPrefs = {
  showWr: true,
  showPnl: true,
  showSymbols: true,
  showWeekCol: true,
};

export interface DailyNote {
  id: string;
  user_id: string;
  note_date: string; // YYYY-MM-DD
  note: string;
  discipline_rating: number | null;
  created_at: string;
  updated_at: string;
}
